import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";

import { Pagination } from "src/components/Pagination";
import { FirebaseDataService } from "src/services/Firebase/data";
import { ClientSpecificLocationForTable, ID } from "src/types";

import { Container, EmptyContainer, NoTicketsMessage, PaginationContainer } from "../../../components/ListScreen";
import { compareBy } from "../../../utils";
import { LocationsTable } from "./LocationsTable";
import {
  CLIENTS_LOCATIONS_BREADCRUMBS,
  DEFAULT_PAGE_SIZE,
  LocationParams,
  LOCATIONS_TABLE_HEADERS,
  MANAGER_CLIENTS_LOCATIONS_BREADCRUMBS,
} from "./constants";
import { Content, ContentFrame, TopBar } from "src/styles";
import { colors } from "src/styles";
import BreadCrumbs from "src/components/BreadCrumbs";
import { generateBreadcrumbs } from "src/helpers/breadcrumbs";
import { useSelector } from "react-redux";
import { getUserData } from "src/redux/selectors/user";
import { CreateButtonWrapper, SearchHeaderContainer } from "./components/styles";
import { SearchInputWrapper } from "src/components/atoms/SearchInput/styles";
import LocationSearch from "./components/LocationSearch";
import Button from "src/components/Button";
import { faDownload } from "@fortawesome/pro-light-svg-icons";
import ExportDateRangeModal from "src/components/organisms/ExportDateRangeModal";
import { ExportDateRange } from "src/components/organisms/ExportDateRangeModal/types";
import styled from "styled-components";

export const Locations: React.FC<LocationParams> = ({ user }) => {
  const navigate = useNavigate();
  const { id: clientId } = useParams();

  const { isClient, clientId: userClientId } = useSelector(getUserData);

  const [locations, setLocations] = useState<ClientSpecificLocationForTable[]>([]);
  const [currentLocationsPage, setCurrentLocationsPage] = useState<ClientSpecificLocationForTable[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [openedLocationsPage, setOpenedLocationsPage] = useQueryParam("openedLocationsPage", NumberParam);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState<string>("");

  const [appliedSearch, setAppliedSearch] = useQueryParam("locationAppliedSearch", StringParam);
  const [searchText, setSearchText] = useQueryParam("locationSearchText", StringParam);
  const [isExportingWashroomLogs, setIsExportingWashroomLogs] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const resolvedClientId = clientId?.toString() || user?.toString() || userClientId?.toString() || "";

  const pageNum = openedLocationsPage || 1;

  const getPageSize = async () => {
    try {
      const { data } = await FirebaseDataService.getEmployeeLocationsPageSize();
      setPageSize(data || DEFAULT_PAGE_SIZE);
    } catch (e) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  };

  const getLocations = async (searchKeyword?: string) => {
    setIsLoading(true);
    const clientIdToUse = resolvedClientId;
    if (clientIdToUse) {
      try {
        const { data: locationsArray } = await FirebaseDataService.getLocationsByClientId(clientIdToUse);

        const sortedLocations = locationsArray.sort(
          (location1: ClientSpecificLocationForTable, location2: ClientSpecificLocationForTable) =>
            compareBy("label", location1, location2),
        );

        const keyword = searchKeyword?.trim().toLowerCase();
        const filteredLocations = keyword
          ? sortedLocations.filter((loc: ClientSpecificLocationForTable) => {
              const searchableText = [loc.label, loc.address, loc.assignedSupervisorName]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
              return searchableText.includes(keyword);
            })
          : sortedLocations;

        const currentPage = openedLocationsPage || 1;
        const locationsPageArray = filteredLocations.slice((currentPage - 1) * pageSize, currentPage * pageSize);

        setLocations(filteredLocations);
        setCurrentLocationsPage(locationsPageArray);
      } catch (e) {
        console.log(e.message);
      }
    }
    setIsLoading(false);
  };

  const onClickApplySearch = async () => {
    setAppliedSearch("true");
    setOpenedLocationsPage(1);
    setSearchText(searchInput);
    await getLocations(searchInput);
  };

  const onClickCancelSearch = () => {
    setAppliedSearch(null);
    setSearchInput("");
    setSearchText(null);
  };

  const onClickClearSearch = async () => {
    setAppliedSearch(null);
    setSearchInput("");
    setSearchText(null);
    setOpenedLocationsPage(1);
    await getLocations("");
  };

  const onPageChangeClick = (pageNumber: number) => {
    setOpenedLocationsPage(pageNumber);
    setCurrentLocationsPage(locations.slice((pageNumber - 1) * pageSize, pageNumber * pageSize));
  };

  const onRowClick = (id: ID, label: string) => {
    navigate(`/washroomlogs/${clientId}/${id}`, { state: { locationLabel: label } });
  };

  useEffect(() => {
    getPageSize().finally(() => getLocations(searchText ?? undefined));
  }, [pageSize, resolvedClientId]);

  const onClickBreadcrumb = (route: ID | null) => typeof route === "string" && navigate(route);

  const onExportButtonClick = () => {
    setIsExportModalOpen(true);
  };

  const onSubmitExportDateRange = async ({ fromDate, toDate }: ExportDateRange): Promise<boolean> => {
    setIsExportingWashroomLogs(true);
    try {
      if (resolvedClientId) {
        const response = await FirebaseDataService.exportWashroomLogs(resolvedClientId, undefined, fromDate, toDate);
        const { fileData, fileName, mimeType } = response.data;

        // Convert base64 to blob
        const byteCharacters = atob(fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        // Trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);

        alert("File exported successfully");
        return true;
      }

      alert("Unable to export washroom logs for this client.");
      return false;
    } catch (error) {
      console.error("Error exporting washroom logs report:", error);
      alert("An error occurred while exporting washroom logs. Please try again.");
      return false;
    } finally {
      setIsExportingWashroomLogs(false);
    }

    return false;
  };

  return (
    <Container>
      <TopBar>
        <BreadCrumbs
          folderStack={
            !isClient
              ? generateBreadcrumbs(MANAGER_CLIENTS_LOCATIONS_BREADCRUMBS, {
                  id: clientId || "id",
                  name: clientId || "name",
                })
              : generateBreadcrumbs(CLIENTS_LOCATIONS_BREADCRUMBS, {
                  id: userClientId || "id",
                  name: userClientId || "name",
                })
          }
          onClickItem={onClickBreadcrumb}
        />
      </TopBar>

      <Content>
        <ContentFrame>
          <SearchHeaderContainer>
            <SearchInputWrapper>
              <LocationSearch
                value={searchInput}
                applied={!!appliedSearch}
                onChange={setSearchInput}
                onClickCancel={onClickCancelSearch}
                onClickApply={onClickApplySearch}
                onClickClear={onClickClearSearch}
              />
            </SearchInputWrapper>
            <CreateButtonWrapper>
              <Button
                text="Export Washroom Logs"
                variant="white"
                loading={isExportingWashroomLogs}
                disabled={isExportingWashroomLogs}
                additionalStyles={{ display: "flex" }}
                icon={faDownload}
                onClick={onExportButtonClick}
              />
            </CreateButtonWrapper>
          </SearchHeaderContainer>

          {isLoading ? (
            <EmptyContainer>
              <Spinner animation="border" size="sm" />
            </EmptyContainer>
          ) : (
            <>
              {currentLocationsPage?.length ? (
                <Panel>
                  <PanelHeader>
                    <PanelTitle>All Locations</PanelTitle>
                    <PanelMeta>{`${currentLocationsPage.length} shown`}</PanelMeta>
                  </PanelHeader>
                  <LocationsTable
                    onRowClick={onRowClick}
                    isLoading={isLoading}
                    headers={LOCATIONS_TABLE_HEADERS}
                    data={currentLocationsPage}
                  />
                </Panel>
              ) : (
                <NoTicketsMessage>No Locations assigned</NoTicketsMessage>
              )}
              <PaginationContainer>
                <Pagination
                  totalCount={locations.length}
                  pageSize={pageSize}
                  onPageChange={onPageChangeClick}
                  currentPage={pageNum}
                />
              </PaginationContainer>
            </>
          )}
        </ContentFrame>
      </Content>

      <ExportDateRangeModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSubmit={onSubmitExportDateRange}
        title="Export Washroom Logs"
        confirmText="Export"
        isSubmitting={isExportingWashroomLogs}
      />
    </Container>
  );
};

const Panel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.grey.light3};
  padding: 12px 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PanelMeta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;
